import { WeatherLayerInfo, MgLayersBase, ChartDepths, LayerGroup, ChartLayersBase } from './mg-layers';
import { LayersFactory } from '@meteogroup/map-layers';
import { default as PlayerCore } from '@meteogroup/player-core';
import { AuthService } from '../services/auth-service';
import { WeatherModel, ForecastInfo } from '../models/weather-model';
import ImageWMSSource from 'ol/source/ImageWMS';
import Layer from 'ol/layer/Layer';
import { VoyageStyle, StyleName } from '../../local_modules/voyage-style/src/voyage-style';
import { MgChartLayersImpl } from './mg-chart-layers';
import { WBChartLayersImpl } from './wb-chart-layers';
import { OSM, Source } from 'ol/source';
import { Tile } from 'ol/layer';
import { getTokenInfo } from '../services/auth-config';
import { SvgIcons } from '../../local_modules/voyage-svg-icons/src/voyage-svg-icons';
import { UserConfigService } from '../services/user-config-services';

const maerskchartsrv = (<any>window)["appenv"].services.maerskchartsrv.baseurl;
const weatherwms = (<any>window)["appenv"].services.weatherwms.baseurl;

const _WeatherElements: [string,string,LayerGroup][] = [
    ['Wind50M','Wind 50 meters',LayerGroup.Wind],
    ['Wind10M','Wind 10 meters',LayerGroup.Wind],
    ['WindGusts50M','Wind gusts 50 meters',LayerGroup.Wind],
    ['WindGusts10M','Wind gusts 10 meters',LayerGroup.Wind],
    ['RiskWindSpeed','Risk wind speed',LayerGroup.Wind],

    ['WavePeriod','Wave period',LayerGroup.SeaAndSwell],
    ['SwellPeriod','Swell period',LayerGroup.SeaAndSwell],
    ['Wave','Significant Wave Height',LayerGroup.SeaAndSwell],
    ['Swell','Swell',LayerGroup.SeaAndSwell],
    ['RiskWaveHeight','Risk wave height',LayerGroup.SeaAndSwell],

    ['Pressure','Pressure',LayerGroup.PressureAndTemp],
    ['AirTemperature','Air temperature',LayerGroup.PressureAndTemp],
    ['SeaWaterTemperature','Sea water temperature',LayerGroup.PressureAndTemp],

    ['DewPoint','Dew point',LayerGroup.Others],
    ['FreezingLevel','Freezing level',LayerGroup.Others],
    ['HPa500','HPa 500',LayerGroup.Others],
    ['Icing','Icing',LayerGroup.Others],
    ['Precipitation','Precipitation',LayerGroup.Others],
    ['RelativeHumidity','Relative humidity',LayerGroup.Others],
    ['IceConcentration','Ice concentration',LayerGroup.Others],
    ['SeaWaterSalinity','Sea water salinity',LayerGroup.Others],
    ['Visibility','Visibility',LayerGroup.Others],
    ['Currents','Currents',LayerGroup.Others],
];

const _WeatherView = [
    'area',
    'line',
    'grid'
];

const _NonGridWeatherElements: [string,string,LayerGroup][] = [
    ['IceBergPosition','Icebergs',LayerGroup.Others],
    ['Hurricanes','Hurricanes',LayerGroup.Others]
    // Disabled as these only work for two hours a day, see https://jiraeu.epam.com/browse/MPM-1655
    //['WeatherFrontData','Weather fronts',LayerGroup.Others],
    //['HighLowPressureSymbols','Pressure symbols',LayerGroup.Others]
];

const env = (<any>window)["appenv"].services.weather;

// EPSG:4326 - Unprojected
// EPSG:3395 - World Mercator
// EPSG:3857 - Pseudo/Web Mercator

const _PROJECTION = (<any>window)["appenv"].projection;

interface StoreObject
{
    depths: ChartDepths;
    borderLayersEnabled: boolean;
    list: WeatherLayerInfo[];
}

interface LayerVisibility {
    element: string,
    area: boolean,
    line: boolean,
    grid: boolean
}

export interface icons {
    wind:string;
    wave:string;
    pressure:string;
    hurricanes:string;
    current:string;
    temperature:string;
    swell:string;
    other: string;
}

export interface BasicWeatherOption {
    label: string;
    name: string;
    element: string;
    type: string;
    checked: boolean;
}

export const basicWeatherOptions: BasicWeatherOption[] = [
    {label: "Wind", name:"wind", element: "Wind50M", type:"grid", checked: false},
    {label: "Wave height", name:"wave", element: "RiskWaveHeight", type:"area", checked: false},
    {label: "Pressure", name:"pressure", element: "Pressure", type:"line", checked: false},
    {label: "Hurricanes", name:"hurricanes", element: "Hurricanes", type:"nongrid", checked: false},
    {label: "Current", name:"current", element: "Currents", type:"grid", checked: false},
    {label: "Air Temperature", name:"temperature", element: "AirTemperature", type:"area", checked: false},
    {label: "Swell", name:"swell", element: "Swell", type:"area", checked: false}, 
];

export const layerIcons = {
    wind: SvgIcons.wind,
    wave: SvgIcons.wave,
    pressure: SvgIcons.pressure,
    hurricanes: SvgIcons.hurricanes,
    current: SvgIcons.current,
    temperature: SvgIcons.temperature,
    swell: SvgIcons.swell,
    other: SvgIcons.otherLayer,
};

export class MgLayersImpl extends MgLayersBase
{
    constructor(playerCore: PlayerCore)
    {
        super();

        this._playerCore = playerCore;

        this._onWeatherUpdated = this._onWeatherUpdated.bind(this);
        this._onTimeUpdate = this._onTimeUpdate.bind(this);
        this._onStylesChanged = this._onStylesChanged.bind(this);
    }

    private _flag: boolean = false; 

    get layers() {
        return this._layers;
    }

    get list() {
        if (!this._initialized) return [];

        const res: WeatherLayerInfo[] = [];

        _WeatherElements.forEach(element => {
            const el: WeatherLayerInfo = {element: element[0], name: element[1], group: element[2]};
            _WeatherView.forEach(viewType => {
                const layer = this._weatherLayersMap.get(`${element[0]}_${viewType}`);
                if (layer) {
                    el[(<"area"|"line"|"grid">viewType)] = {
                        visible: layer.getVisible(),
                        opacity: layer.getOpacity()
                    };
                }
            });
            res.push(el);
        });

        _NonGridWeatherElements.forEach(element => {
            const el: WeatherLayerInfo = {element: element[0], name: element[1], group: element[2]};
            const layer = this._weatherLayersMap.get(`${element[0]}_nongrid`);
            el.nongrid = {
                visible: layer && layer.getVisible() ? true : false
            };
            res.push(el);
        });

        return res;
    }

    get chartDepths() {
        return this._depths;
    }

    get borderLayersEnabled() {
        return this._borderLayersEnabled;
    }

    clearAdvanceOptions(list: WeatherLayerInfo[]): void {
        this._flag = true;  // Suppress _store method

        list.forEach((el) => {
            this.setVisible(el.element, 'grid', false);
            this.setVisible(el.element, 'line', false);
            this.setVisible(el.element, 'area', false);

            if (el.nongrid) {
                this.setVisible(el.element, 'nongrid', false);
            }
        });

        this._flag = false;  // Re-enable _store method
        this._store(); 
    }

    clearBasicOptions() {
        this._flag = true;  // Suppress _store method

        basicWeatherOptions.forEach((el) => {
            this.setVisible(el.element, el.type, false);
        });

        this._flag = false;  // Re-enable _store method
        this._store();
    }

    setVisible(element: string, type: string, visible: boolean) {
        const weatherLayer = this._weatherLayersMap.get(`${element}_${type}`);
        
        if (weatherLayer) {
            weatherLayer.setVisible(visible);
            this._setBorderLayer(element);
            if (!this._flag) {
                this._store();
            }
        }
    }

    setOpacity(element: string, type: string, opacity: number) {
        const layer = this._weatherLayersMap.get(`${element}_${type}`);
        if (layer) {
            layer.setOpacity(opacity);
            this._store();
        }
    }

    toggleBorderLayers(visible: boolean) {
        this._borderLayersEnabled = visible;
        this.list.forEach((el)=> {
            this._setBorderLayer(el.element)
        })
        this._store();
    }

    setChartDepths(depths: ChartDepths) {
        this._depths = depths;

        this._chartLayers?.setChartDepths(depths);

        this._store();
    }

    async initialize(mapConfig: any) {
        this._layers = [];
        WeatherModel!.addEventListener("weather-updated", this._onWeatherUpdated);
        this._playerCore.addEventListener("timeupdate", this._onTimeUpdate);
        VoyageStyle.addEventListener("styles-changed", this._onStylesChanged)
        this._setForecastInfo(WeatherModel!.latestForecastInfo);
        const forecastVersion = this._forecastInfo ? this._forecastInfo.name : WeatherModel!.climaticForecast;
        this._usingClimaticForecast = forecastVersion === WeatherModel!.climaticForecast;
        this._onStylesChanged();

        const authLoader = async (url: string, signal: AbortSignal) => {

            let accessToken;

            // if ((<any>window)["appenv"].authRequired) accessToken = await AuthService.getAccessToken(env.scope);
            if ((<any>window)["appenv"].authRequired) accessToken = getTokenInfo();

            return fetch(url, {
                mode: 'cors',
                cache: 'no-cache',
                method: 'GET',
                signal: signal,
                headers: accessToken ? {
                    'Authorization': `Bearer ${accessToken}`
                } : {}
            });
        }

        this._layersFactory = new LayersFactory({
            chartWMSUrl: maerskchartsrv,
            weatherWMSUrl: weatherwms + '/api/{version}/{format}/{forecast}/{element}?view={view}&time={time}&z={z}&x={x}&y={y}',
            weatherNonGridUrl: weatherwms + '/api/{version}/{format}/nongrid/{forecast}/{element}',
            weatherBorderWMSUrl: weatherwms + '/api/{version}/{format}/{forecast}/{element}/areas',
            featureLoader: authLoader,
            player: this._playerCore,
        });

        if ((<any>window)["appenv"].featureToggles.encTileServer) {
            this._chartLayers = new WBChartLayersImpl();
        } else {
            this._chartLayers = new MgChartLayersImpl(this._layersFactory);
        }

        if(mapConfig.simplified == "true"){
             this._layers.push(new Tile({source: new OSM({
                url: "https://tile.openstreetmap.de/{z}/{x}/{y}.png",
             })}));
        }else{
            await this._chartLayers.initialize(mapConfig);
            this._layers.push(...this._chartLayers.layers);
        }
        

        // grid, isoline and area layers
        _WeatherElements.forEach(element => {

            const border = this._createBorderLayer(element, forecastVersion);
            const area = this._createWeatherWMSLayer(element, 'area', forecastVersion)
            const line = this._createWeatherWMSLayer(element, 'line', forecastVersion)
            const grid = this._createWeatherWMSLayer(element, 'grid', forecastVersion)
            
            this._layers.push(border);
            this._layers.push(area);
            this._layers.push(line);
            this._layers.push(grid);
            
            this._borderLayersMap.set(element[0], border)
            this._weatherLayersMap.set(`${element[0]}_area`, area);
            this._weatherLayersMap.set(`${element[0]}_line`, line);
            this._weatherLayersMap.set(`${element[0]}_grid`, grid);

            this._layersVisibility.push({
                element: element[0],
                area: false,
                line: false,
                grid: false
            })

        });

        _NonGridWeatherElements.forEach(element => {
            const layer = this._layersFactory!.createWeatherVectorLayer({
                title: `${element[1]}`,
                timeBucketMs: 'hourly',
                projection: _PROJECTION,
                maxZoom: 10,
                params: {
                  element: element[0],
                  forecast: forecastVersion,
                }
            });

            layer.setVisible(false);
            this._layers.push(layer);
            this._weatherLayersMap.set(`${element[0]}_nongrid`, layer);
        });

        this._initialized = true;
        this._load();
    }

    zoomLevelChanged(zoom: number) {
        this._chartLayers?.zoomLevelChanged(zoom);
        this.dispatchEvent(new CustomEvent('zoom-level-changed', {detail : {zoom}}))
    }


    private _playerCore: PlayerCore;
    private _initialized: boolean = false;
    private _layers: Layer<Source>[] = [];
    private _forecastInfo: ForecastInfo|null = null;
    private _forecastTimeframe: [number, number] = [ -1, -1 ];
    private _usingClimaticForecast: Boolean = false;
    private _depths: ChartDepths = {
        safetyContour: 20,
        shallowContour: 15,
        deepContour: 30,
        safetyDepth: 20
    };
    private _weatherLayersMap: Map<string, Layer<Source>> = new Map();
    private _layersFactory: LayersFactory|null = null;
    private _chartLayers: ChartLayersBase|null = null;
    private _layersVisibility: LayerVisibility[] = [];
    private _borderLayersMap: Map<string, Layer<Source>> = new Map();
    private _borderLayersEnabled: boolean = false;

    private async _store() {
        const res: StoreObject = {
            depths: this._depths,
            borderLayersEnabled: this._borderLayersEnabled,
            list: this.list
        };

        // setting weather limits
        await UserConfigService.upsertUserMapView({"type": "weather", "user_id": AuthService.getCurrentUserId(), "data": res});
        this.dispatchEvent(new Event('change'));
    }

    private async _load() {
        try {
            const res: StoreObject = await UserConfigService.getUserWeatherView(AuthService.getCurrentUserId());
            if (!res || !res.list || !res.depths) {
                this._store();
                throw new Error("failed to load map layers from cache")
            }
            this._borderLayersEnabled = res.borderLayersEnabled;

            // set layers visibility
            for (const el of res.list) {
                const visibilityMap = {
                  grid: el.grid,
                  line: el.line,
                  area: el.area,
                  nongrid: el.nongrid
                };
              
                for (const [key, item] of Object.entries(visibilityMap)) {
                  if (item && item.visible) {
                    this.setVisible(el.element, key, true);
                  }
                }
              
                if (el.nongrid?.opacity) {
                  this.setOpacity(el.element, 'nongrid', el.nongrid.opacity);
                }
              }
              
            this.setChartDepths(res.depths);
        }
        catch (error) {
            this._store();
        }

        this.dispatchEvent(new Event('change'));
    }


    private _setForecastInfo(info: ForecastInfo|null) {
        this._forecastInfo = info;
        if (info) {
            this._forecastTimeframe = [ new Date(info.forecast_from).getTime(), new Date(info.forecast_to).getTime() ];
        }
    }

    private _onWeatherUpdated() {
        const forecastInfo = WeatherModel!.latestForecastInfo;

        if (forecastInfo && (!this._forecastInfo || this._forecastInfo.name !== forecastInfo.name)) {
            this._setForecastInfo(forecastInfo);

            // Check if new forecast makes us go from climatic to real forecast
            if (this._usingClimaticForecast &&
                this._playerCore.currentTime >= this._forecastTimeframe[0] && this._playerCore.currentTime <= this._forecastTimeframe[1])
            {
                this._usingClimaticForecast = false;
            }

            for(let [key, layer] of this._weatherLayersMap) {
                if (key.endsWith("nongrid")) {
                    (<ImageWMSSource>layer.getSource()).updateParams({ forecast: this._forecastInfo!.name });
                    // These sources don't automatically refresh when setting a new forecast - explicitly refresh here
                    (<ImageWMSSource>layer.getSource()).refresh();
                } else if (!this._usingClimaticForecast) {
                    (<any>layer.getSource()).timeBucketType = 'hourly';
                    (<ImageWMSSource>layer.getSource()).updateParams({ forecast: this._forecastInfo!.name });
                }
            }

            for (let [key, layer] of this._borderLayersMap) {
                (<ImageWMSSource>layer.getSource()).updateParams({ forecast: this._forecastInfo!.name });
                (<ImageWMSSource>layer.getSource()).refresh();
            }
        }
    }

    private _onTimeUpdate()
    {
        if (this._forecastInfo)
        {
                if (this._playerCore.currentTime >= this._forecastTimeframe[0] && this._playerCore.currentTime <= this._forecastTimeframe[1])
                {
                    if (this._usingClimaticForecast)
                    {
                        // Switch from climatic forecast to real forecast
                        for(const [key, layer] of this._weatherLayersMap)
                        {
                            // non-grid elements never use climatic weather, so skip those
                            if (!key.endsWith("nongrid"))
                            {
                                (<any>layer.getSource()).timeBucketType = 'hourly';
                                // Let the layers apply the time change before changing the forecast parameter
                                setTimeout( () => {
                                    (<ImageWMSSource>layer.getSource()).updateParams({ forecast: this._forecastInfo!.name });
                                });
                            }
                        }
                        this._usingClimaticForecast = false;
                        
                        // refresh borders visibility (should be visible when using real forecast)
                        this.list.forEach((el)=> {
                            this._setBorderLayer(el.element)
                        })
                    }
                }
                else if (!this._usingClimaticForecast)
                {
                    // Switch from real forecast to climatic
                    for(const [key, layer] of this._weatherLayersMap)
                    {
                        // non-grid elements can't use climatic weather, so skip those
                        if (!key.endsWith("nongrid"))
                        {
                            (<any>layer.getSource()).timeBucketType = 'monthly';
                            // Let the layers apply the time change before changing the forecast parameter
                            setTimeout( () => {
                                (<ImageWMSSource>layer.getSource()).updateParams({ forecast: WeatherModel!.climaticForecast });
                            });
                        }
                    }
                    this._usingClimaticForecast = true;

                    // refresh borders visibility (should be invisible when using climatic forecast)
                    this.list.forEach((el)=> {
                        this._setBorderLayer(el.element)
                    })
                }
        }
    }

    private _onStylesChanged()
    {
        if (this._chartLayers) {
            this._chartLayers.setColorScheme(VoyageStyle.currentStyle);
        }
    }

    private _createWeatherWMSLayer 
    (
        element: [string, string, LayerGroup],
        viewType: string,
        forecastVersion: string,
    )
    {
        const layer = this._layersFactory!.createWeatherWMSLayer
        ({
            title: `${element[1]} (${viewType})`,
            opacity: viewType === 'area' ? 0.5 : 1.0,
            timeBucketMs: 'hourly',
            preloadTimeMs: 2 * 60 * 60 * 1000,
            projection: _PROJECTION,
            maxZoom: 9,
            params: {
              view: viewType,
              element: element[0],
              forecast: forecastVersion,
              additionalOptions: {
                  plot_extrema: true,
                  unit: 'kts'
              },
            unit: 'kts',
        }
        });

        layer.setVisible(false);
        return layer;
    }

    private _createBorderLayer
    (
        element: [string, string, LayerGroup],
        forecastVersion: string,
        visible = false
    ) 
    {
        // don't set title (ex. title: `${element.name} (${viewMode})`, )to hide layer on switcher control
        const layer = this._layersFactory!.createWeatherBorderWMSLayer({
            visible,
            projection: _PROJECTION,
            displayInLayerSwitcher: false,
            zIndex: 10,
            params: {
                view: 'border',
                element: element[0],
                forecast: forecastVersion,
            },
        });
        return layer;
    }

    private _setBorderLayer (element: string) {
        const borderLayer = this._borderLayersMap.get(element);

        if (borderLayer) {
            if(!this._borderLayersEnabled) {
                borderLayer.setVisible(false)
                
            } else {
                let viewTypesVisibilityInfo: WeatherLayerInfo;
                const layerList = this.list;

                // get visibility info of current weather element across grid, line and area view types
                for (let i = 0; i < layerList.length; i++) {
                    if (layerList[i].element === element) {
                        viewTypesVisibilityInfo = layerList[i];
                    }
                }

                if (!this._usingClimaticForecast && (viewTypesVisibilityInfo!.grid!.visible || viewTypesVisibilityInfo!.line!.visible  || viewTypesVisibilityInfo!.area!.visible) ) {
                    borderLayer.setVisible(true)
                }  else {
                    borderLayer.setVisible(false)
                }
            }
        }
    }
}
